import { useState } from 'react';
import { CardTitle } from '@shared/ui/Card';
import { FileDropZone } from '@shared/ui/FileDropZone';
import { useUploadMeetingProtocolDocMutation } from '@shared/api';
import {
  UploadCard,
  UploadDesc,
  FormRow,
  TitleInput,
  ContextSelect,
  Note,
} from './styles';
import { PROTOCOL_DOC_ACCEPT, type UploadContextProps } from './types';

/** Панель загрузки готового протокола (DOCX/TXT/PDF) — совещание без аудиозаписи (U).
 *  Текст извлекается и структурируется LLM (без ASR). */
export function UploadProtocolPanel({ seriesList, projects }: UploadContextProps) {
  const [upload, { isLoading }] = useUploadMeetingProtocolDocMutation();
  const [title, setTitle] = useState('');
  const [seriesId, setSeriesId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [done, setDone] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function handleFile(file: File) {
    setError(null);
    setDone(null);
    const form = new FormData();
    form.append('file', file);
    if (title.trim()) form.append('title', title.trim());
    if (seriesId) form.append('series_id', seriesId);
    if (projectId) form.append('project_id', projectId);
    try {
      const res = await upload(form).unwrap();
      setDone(res.meeting.title);
      setTitle('');
    } catch (err) {
      setError('Не удалось загрузить протокол. Проверьте файл и попробуйте снова.');
      // eslint-disable-next-line no-console
      console.error('uploadMeetingProtocolDoc failed', err);
    }
  }

  return (
    <UploadCard>
      <div>
        <CardTitle as="span">Загрузить готовый протокол</CardTitle>
        <UploadDesc>
          Уже готовый протокол (DOCX/TXT/PDF) для совещания без аудиозаписи. Текст автоматически
          разбирается на решения, поручения, исполнителей и сроки.
        </UploadDesc>
      </div>
      <FormRow>
        <TitleInput
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="Название совещания (необязательно)"
          aria-label="Название совещания (протокол)"
        />
        <ContextSelect
          value={seriesId}
          onChange={(e) => setSeriesId(e.target.value)}
          aria-label="Серия совещаний (протокол)"
        >
          <option value="">— без серии —</option>
          {seriesList.map((s) => (
            <option key={s.id} value={s.id}>
              {s.title}
            </option>
          ))}
        </ContextSelect>
        {projects.length > 0 && (
          <ContextSelect
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            aria-label="Строительный объект (протокол)"
          >
            <option value="">— без привязки к объекту —</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </ContextSelect>
        )}
      </FormRow>
      <FileDropZone
        accept={PROTOCOL_DOC_ACCEPT}
        hint="Перетащите готовый протокол (docx/txt/pdf) или выберите его."
        onFile={handleFile}
      />
      {isLoading && <Note $tone="muted">Загружаю протокол…</Note>}
      {done && (
        <Note $tone="success">
          Протокол принят: «{done}». Идёт выделение решений и поручений.
        </Note>
      )}
      {error && <Note $tone="danger">{error}</Note>}
    </UploadCard>
  );
}
